import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';
import { DEFAULT_POI_SEARCH_RADIUS_PRESET_ID } from '@/db/data/initialPoiSearchRadiusPresetsData';

function normalizeLabel (value) {
  return String(value || '').trim();
}

function normalizeSortOrder (value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.round(numeric));
}

function normalizeKm (value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric;
}

function sortPresets (rows) {
  return [...rows].sort((first, second) => {
    const firstOrder = normalizeSortOrder(first?.sortOrder, 0);
    const secondOrder = normalizeSortOrder(second?.sortOrder, 0);
    if (firstOrder !== secondOrder) return firstOrder - secondOrder;

    const firstLabel = String(first?.label || '').toLowerCase();
    const secondLabel = String(second?.label || '').toLowerCase();
    if (firstLabel !== secondLabel) return firstLabel.localeCompare(secondLabel);

    return Number(first?.id || 0) - Number(second?.id || 0);
  });
}

export const usePoiSearchRadiusPresetsStore = defineStore('poiSearchRadiusPresets', {
  state: () => ({
    presets: [],
    isLoaded: false
  }),

  getters: {
    getAllPoiSearchRadiusPresets: (state) => state.presets,
    getPoiSearchRadiusPresetById: (state) => (id) => {
      const numericId = Number(id);
      if (!Number.isFinite(numericId)) return null;
      return state.presets.find((preset) => Number(preset?.id) === numericId) || null;
    },
    getDefaultPoiSearchRadiusPreset: (state) => {
      return state.presets.find((preset) => Number(preset?.id) === DEFAULT_POI_SEARCH_RADIUS_PRESET_ID)
        || state.presets[0]
        || null;
    }
  },

  actions: {
    async loadPoiSearchRadiusPresets () {
      try {
        const rows = await GeoMoverDB.findAll('poiSearchRadiusPresets');
        this.presets = sortPresets(Array.isArray(rows) ? rows : []);
        this.isLoaded = true;
        return this.presets;
      }
      catch (error) {
        appDebugLog.error('PoiSearchRadiusPresetsStore', 'Error loading POI search radius presets:', error);
        this.presets = [];
        this.isLoaded = false;
        return [];
      }
    },

    async createPoiSearchRadiusPreset (input = {}) {
      if (!this.isLoaded) await this.loadPoiSearchRadiusPresets();

      const label = normalizeLabel(input.label);
      if (!label) throw new Error('Preset label is required.');

      const km = input.km === null ? null : normalizeKm(input.km);
      if (input.km !== null && km === null) {
        throw new Error('Preset distance must be a positive number or empty for Global.');
      }

      const duplicate = this.presets.find((preset) => String(preset?.label || '').toLowerCase() === label.toLowerCase());
      if (duplicate) throw new Error('Preset label must be unique.');

      const now = Date.now();
      const payload = {
        label,
        km,
        sortOrder: normalizeSortOrder(input.sortOrder, (this.presets.length + 1) * 10),
        createdAt: now,
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne('poiSearchRadiusPresets', payload);
      const persisted = { ...payload, id };
      this.presets = sortPresets([...this.presets, persisted]);
      this.isLoaded = true;
      return persisted;
    },

    async updatePoiSearchRadiusPreset (presetId, updates = {}) {
      const numericId = Number(presetId);
      if (!Number.isFinite(numericId)) throw new Error('Valid preset ID is required.');

      if (!this.isLoaded) await this.loadPoiSearchRadiusPresets();

      const current = this.getPoiSearchRadiusPresetById(numericId) || await GeoMoverDB.findOne('poiSearchRadiusPresets', numericId);
      if (!current) throw new Error('Radius preset not found.');

      const label = updates.label === undefined ? current.label : normalizeLabel(updates.label);
      if (!label) throw new Error('Preset label is required.');

      const nextKm = updates.km === undefined
        ? current.km
        : (updates.km === null ? null : normalizeKm(updates.km));
      if (updates.km !== undefined && updates.km !== null && nextKm === null) {
        throw new Error('Preset distance must be a positive number or empty for Global.');
      }

      const duplicate = this.presets.find((preset) => (
        Number(preset?.id) !== numericId
        && String(preset?.label || '').toLowerCase() === label.toLowerCase()
      ));
      if (duplicate) throw new Error('Preset label must be unique.');

      const next = {
        ...current,
        label,
        km: nextKm,
        sortOrder: updates.sortOrder === undefined
          ? normalizeSortOrder(current.sortOrder, 0)
          : normalizeSortOrder(updates.sortOrder, 0),
        updatedAt: Date.now()
      };

      await GeoMoverDB.updateOne('poiSearchRadiusPresets', next, numericId);
      this.presets = sortPresets([
        ...this.presets.filter((preset) => Number(preset?.id) !== numericId),
        next
      ]);
      this.isLoaded = true;
      return next;
    },

    async deletePoiSearchRadiusPreset (presetId) {
      const numericId = Number(presetId);
      if (!Number.isFinite(numericId)) throw new Error('Valid preset ID is required.');

      if (!this.isLoaded) await this.loadPoiSearchRadiusPresets();

      if (this.presets.length <= 1) {
        throw new Error('At least one POI search radius preset must remain.');
      }

      await GeoMoverDB.deleteOne('poiSearchRadiusPresets', numericId);
      this.presets = sortPresets(this.presets.filter((preset) => Number(preset?.id) !== numericId));
      this.isLoaded = true;
      return true;
    },

    async reorderPoiSearchRadiusPresets (orderedIds = []) {
      if (!this.isLoaded) await this.loadPoiSearchRadiusPresets();

      const nextIds = orderedIds
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      if (nextIds.length !== this.presets.length) {
        throw new Error('Preset reorder payload is incomplete.');
      }

      const currentIds = new Set(this.presets.map((preset) => Number(preset?.id)));
      if (nextIds.some((id) => !currentIds.has(id))) {
        throw new Error('Preset reorder payload contains unknown IDs.');
      }

      const now = Date.now();
      const nextRows = nextIds.map((id, index) => {
        const current = this.getPoiSearchRadiusPresetById(id);
        return {
          ...current,
          sortOrder: (index + 1) * 10,
          updatedAt: now
        };
      });

      for (const row of nextRows) {
        await GeoMoverDB.updateOne('poiSearchRadiusPresets', row, Number(row.id));
      }

      this.presets = sortPresets(nextRows);
      this.isLoaded = true;
      return this.presets;
    }
  }
});
